import React, { useCallback, useRef, useEffect } from 'react';
import { useField } from 'react-final-form';
import cn from 'classnames';

import Avatar from 'Shared/components/Avatar/Avatar';
import { convertFileToBase64, getInitials } from 'Shared/helpers';
import { useFieldValue } from 'Shared/utils/hooks';
import { createFormField } from 'Shared/utils/createFormField';
import { acceptedProfilePictureFormats } from 'Shared/constants/constants';
import { WpIcon } from 'Wpds/components/icon';

import './FormChooseAvatar.scss';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

type Props = {
  onChange: Function;
};

function ChooseAvatar({ onChange }: Props) {
  const inputRef = useRef(null);
  const {
    input: { onChange: onProfilePictureChange },
  } = useField('profilePicture');
  const {
    input: { onChange: onInititalsChange },
  } = useField('profile.avatar.initials');

  const fullName = useFieldValue('profile.fullName');
  const avatar = useFieldValue('profile.avatar');
  const {
    input: { value: avatarType, onChange: onAvatarTypeChange },
  } = useField('profile.avatarType');

  const handleAddPhotoClick = useCallback(() => inputRef.current.click(), [inputRef]);

  const handleInputFileChange = useCallback(
    async event => {
      const file = event.target.files[0];
      onProfilePictureChange(file);
      onAvatarTypeChange('picture');
      const url = await convertFileToBase64(file);
      onChange(url);
    },
    [onProfilePictureChange, onAvatarTypeChange, onChange]
  );

  useEffect(() => {
    onInititalsChange(getInitials(fullName));
  }, [fullName]);

  const handleRemoveAvatarClick = () => {
    onChange(null);
    onAvatarTypeChange('initials');
  };

  return (
    <>
      <div className="form-choose-avatar d-flex ml-1">
        <div
          className={cn(
            'form-choose-avatar-preview form-choose-avatar-default form-choose-avatar-add cursor-pointer position-relative',
            {
              'form-choose-avatar-default--checked': !avatar.url,
            }
          )}
        >
          <Avatar
            avatar={{
              profilePictureUrl: avatar.url,
              color: avatar.color,
              initials: avatar.initials,
            }}
            height={58}
          />
          {avatar.url && (
            <div
              className="form-choose-avatar-remove align-items-center justify-content-center circle position-absolute bg-white"
              onClick={handleRemoveAvatarClick}
            >
              <WpIcon name="times-circle-o" aria-hidden="true" />
            </div>
          )}
        </div>
        <WpTooltip>
          <WpTooltipTrigger>
            <div
              className={cn('form-choose-avatar-default form-choose-avatar-add ', {
                'form-choose-avatar-default--checked': avatarType === 'picture',
              })}
              onClick={handleAddPhotoClick}
            >
              <span className="form-choose-avatar-add-upload-icon">
                <WpIcon name="upload" />
              </span>
              <p className="form-choose-avatar-add-caption m-0 mt-1">Upload</p>
              <p className="form-choose-avatar-add-caption m-0">photo</p>
            </div>
          </WpTooltipTrigger>
          <WpTooltipContent>Add photo</WpTooltipContent>
        </WpTooltip>
      </div>
      <input
        className="d-none"
        type="file"
        accept={acceptedProfilePictureFormats}
        onChange={handleInputFileChange}
        ref={inputRef}
      />
    </>
  );
}

export default createFormField(ChooseAvatar);
